import { useState } from 'react'
import { FileText, Download, Trash2 } from 'lucide-react'
import { getAuditLog, clearAuditLog } from '../../services/audit.service'
import type { AuditEntry } from '../../types/auth.types'

const ACTION_COLOR: Record<string, string> = {
  // Auth
  LOGIN:              '#e8a000',
  LOGOUT:             '#555555',
  // User management
  CREATE_USER:        '#ffc600',
  UPDATE_USER:        '#ff8c00',
  DELETE_USER:        '#cc3300',
  // Permissions
  GRANT_PERMISSION:   '#e8a000',
  REVOKE_PERMISSION:  '#cc3300',
  UPDATE_PERMISSIONS: '#ff8c00',
  // Impersonation
  IMPERSONATE_START:  '#cc3300',
  IMPERSONATE_STOP:   '#555555',
  // Tasks
  TASK_CREATED:       '#ffc600',
  TASK_STATUS_UPDATE: '#ff8c00',
}

function exportCSV(logs: AuditEntry[]) {
  const header = 'ID,User,Role,Action,Module,Detail,Timestamp\n'
  const rows = logs.map(l =>
    `${l.id},"${l.userName}",${l.userRole},${l.action},${l.module},"${l.detail}",${new Date(l.timestamp).toISOString()}`
  ).join('\n')
  const blob = new Blob([header + rows], { type: 'text/csv' })
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
  a.download = `clickbait-audit-${Date.now()}.csv`; a.click()
}

export default function AuditLogPanel() {
  const [filterUser, setFilterUser]     = useState('')
  const [filterModule, setFilterModule] = useState('')
  const [filterAction, setFilterAction] = useState('')
  const [, setVersion] = useState(0)  // bumping this forces a re-render → re-reads log

  const logs = getAuditLog()  // reads latest from localStorage every render
  const users   = [...new Set(logs.map(l => l.userName))]
  const modules = [...new Set(logs.map(l => l.module))]
  const actions = [...new Set(logs.map(l => l.action))]

  const filtered = logs.filter(l =>
    (!filterUser   || l.userName === filterUser) &&
    (!filterModule || l.module   === filterModule) &&
    (!filterAction || l.action   === filterAction)
  )

  const selStyle: React.CSSProperties = {
    background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.15)',
    borderRadius: 8, padding: '7px 10px', color: '#a0a0a0',
    fontSize: 12, outline: 'none', fontFamily: 'DM Sans, sans-serif',
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <FileText size={20} style={{ color: '#ffc600' }} />
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Audit Log</h1>
            <p className="text-sm" style={{ color: '#a0a0a0' }}>{filtered.length} entries</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportCSV(filtered)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ border: '1px solid rgba(255,198,0,0.3)', color: '#ffc600', background: 'transparent' }}>
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={() => {
              if (window.confirm('Clear all audit entries? This cannot be undone.')) {
                clearAuditLog(); setVersion(v => v + 1)
              }
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ border: '1px solid rgba(204,51,0,0.25)', color: '#cc3300', background: 'transparent' }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select style={selStyle} value={filterUser} onChange={e => setFilterUser(e.target.value)}>
          <option value="">All Users</option>
          {users.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select style={selStyle} value={filterModule} onChange={e => setFilterModule(e.target.value)}>
          <option value="">All Modules</option>
          {modules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select style={selStyle} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
          <option value="">All Actions</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {(filterUser || filterModule || filterAction) && (
          <button onClick={() => { setFilterUser(''); setFilterModule(''); setFilterAction('') }}
            className="text-xs px-3 py-1.5 rounded-lg" style={{ color: '#cc3300', border: '1px solid rgba(204,51,0,0.25)' }}>
            Clear
          </button>
        )}
      </div>

      {/* Log table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
        {filtered.length === 0 ? (
          <div className="text-center py-16" style={{ background: '#0f0f0f', color: '#333' }}>
            {logs.length === 0 ? 'No actions logged yet — log in and out to generate entries.' : 'No entries match your filters.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.1)' }}>
                {['Time', 'User', 'Action', 'Module', 'Detail'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#a0a0a0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => (
                <tr key={entry.id} style={{ background: '#0f0f0f', borderBottom: '1px solid rgba(255,198,0,0.05)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1a1a1a')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#0f0f0f')}>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap" style={{ color: '#555' }}>
                    {new Date(entry.timestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-white">{entry.userName}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs px-2 py-0.5 rounded font-mono"
                      style={{ background: `${ACTION_COLOR[entry.action] ?? '#555'}18`, color: ACTION_COLOR[entry.action] ?? '#a0a0a0' }}>
                      {entry.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: '#a0a0a0' }}>{entry.module}</td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: '#555' }}>{entry.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
